import { useEffect, useState } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'
import Markdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'

import { Button, Divider, EmptyState, Icon, Layout, Page, Text } from '@loadsmart/miranda-react'

import analyticsLibraryIllustration from 'assets/images/analytics-library-illustration.svg'
import { AnalyticsEvent } from 'core/analytics/events'
import { useAnalytics } from 'core/analytics/useAnalytics'
import { RequestType } from 'core/domain/RequestType'
import AppLayout from 'core/ui/components/AppLayout'
import Header from 'core/ui/components/Header'
import ZendeskDialog from 'core/ui/components/zendesk/TryNowZendeskDialog'
import {
  demoLibraryTabLabel,
  filterDemoSections,
  groupDemoReportsByCategory,
} from 'managed-analytics/domain/demoLibrary'
import LibraryToolbar, {
  ALL_REPORTS_TAB,
  type LibraryTab,
} from 'managed-analytics/ui/components/libraryToolbar/LibraryToolbar'
import { getPBIDemoReportsQueryOptions } from 'managed-analytics/use-cases/getPBIReports'

import {
  StyledContainer,
  StyledContentColumn,
  StyledEmptyResults,
  StyledHeroIllustration,
  StyledReportDescription,
  StyledReportImage,
} from './AnalyticsLibraryPage.styles'

const TRACK_EVENT_EXPIRY_MS = 1 * 60 * 1000

const AnalyticsLibrary = () => {
  const demoReportsQuery = useSuspenseQuery(getPBIDemoReportsQueryOptions())

  const demoSections = groupDemoReportsByCategory(demoReportsQuery.data)
  // The segmented control only earns its space once reports are tagged across
  // more than one product. Until then they all land in a single group, which
  // should read as one plain list.
  const hasProductTabs = demoSections.length > 1

  const [activeTab, setActiveTab] = useState<string>(ALL_REPORTS_TAB)
  const [searchQuery, setSearchQuery] = useState('')

  // The categories come from the data, so a tab can disappear under us on a
  // refetch. Fall back to the whole catalog rather than an empty page.
  const isKnownTab =
    activeTab === ALL_REPORTS_TAB || demoSections.some(([category]) => category === activeTab)
  const selectedTab = hasProductTabs && isKnownTab ? activeTab : ALL_REPORTS_TAB

  const tabs: Array<LibraryTab> = hasProductTabs
    ? [
        { id: ALL_REPORTS_TAB, label: 'All reports', count: demoReportsQuery.data.length },
        ...demoSections.map(([category, reports]) => ({
          id: category,
          label: demoLibraryTabLabel(category),
          count: reports.length,
        })),
      ]
    : []

  const selectedSections =
    selectedTab === ALL_REPORTS_TAB
      ? demoSections
      : demoSections.filter(([category]) => category === selectedTab)
  const visibleSections = filterDemoSections(selectedSections, searchQuery)

  // A single product's tab is its own heading; only the "All reports" view
  // needs the sections named.
  const showSectionHeadings = hasProductTabs && selectedTab === ALL_REPORTS_TAB

  const [zendeskDialogRequestType, setZendeskDialogRequestType] = useState<RequestType | null>(null)
  const [isZendeskDialogOpen, setIsZendeskDialogOpen] = useState(false)

  const { track } = useAnalytics()

  const navigate = useNavigate()

  useEffect(() => {
    const userAgent = window.navigator.userAgent
    const isMobile = /Mobi/i.test(userAgent)

    if (isMobile) {
      navigate('/mobile-not-supported')
    }
  }, [navigate])

  // This code is functionally equivalent to what was previously here
  // but we're now using react query to fetch the data
  // We're still tracking the event after the data is loaded
  useEffect(() => {
    if (!demoReportsQuery.data) return

    function shouldTrackEvent() {
      const lastTracked = localStorage.getItem('lastTrackedDemoEvent')
      if (!lastTracked) return true

      const now = Date.now()
      return now - parseInt(lastTracked, 10) > TRACK_EVENT_EXPIRY_MS
    }

    if (shouldTrackEvent()) {
      track(AnalyticsEvent.OpenDemo)
      localStorage.setItem('lastTrackedDemoEvent', Date.now().toString())
    }
  }, [demoReportsQuery.data, track])

  return (
    <AppLayout>
      <Header unauthCtaLabel="Sign in" unauthCtaHref="/" />
      <Page.Content>
        <Page.Body>
          <StyledContentColumn gap="spacing-6">
            <StyledContainer
              backgroundColor="color-background-primary"
              borderColor="color-border"
              borderWidth="border-thin"
              padding="spacing-6"
            >
              <Layout.Group align="center" gap="spacing-5">
                <Layout.Stack gap="spacing-4" style={{ flex: 1, minWidth: 0 }}>
                  <Text
                    variant="hero-sm-bold"
                    color="color-primary-100"
                    style={{ lineHeight: '125%', maxWidth: '24ch' }}
                  >
                    Powerful, customizable BI reporting and data workflows at your fingertips.
                  </Text>
                  <Text variant="body-md" color="color-text-tertiary">
                    Access ready-made or fully customizable exportable reports built around
                    objective problem statements, actionable data, and specific to your unique
                    business rules. Leverage our professional services to automate data workflows
                    and centralize your data in one place. Manage multi-user access both inside and
                    outside your org. Partner with Loadsmart to develop a value-driven data strategy
                    where transportation becomes your competitive weapon.
                  </Text>
                  <Layout.Group>
                    <Button
                      variant="primary"
                      onClick={() => {
                        setZendeskDialogRequestType(RequestType.Question)
                        setIsZendeskDialogOpen(true)
                      }}
                    >
                      Talk to an expert
                    </Button>
                    <Button
                      variant="tertiary"
                      leading={<Icon name="arrow-sort-down" />}
                      onClick={() => {
                        document
                          .getElementById('report-list')
                          ?.scrollIntoView({ behavior: 'smooth' })
                      }}
                    >
                      Explore & preview custom reports ({demoReportsQuery.data.length})
                    </Button>
                  </Layout.Group>
                </Layout.Stack>
                <StyledHeroIllustration src={analyticsLibraryIllustration} alt="" />
              </Layout.Group>
            </StyledContainer>

            <LibraryToolbar
              tabs={tabs}
              activeTab={selectedTab}
              onTabChange={setActiveTab}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
            />

            <Layout.Stack id="report-list" gap="spacing-6">
              {visibleSections.length === 0 ? (
                <StyledEmptyResults>
                  <EmptyState variant="page">
                    <EmptyState.Header>No reports match your search</EmptyState.Header>
                    <EmptyState.Message>
                      Try a different word, or clear the search to see the whole catalog.
                    </EmptyState.Message>
                  </EmptyState>
                </StyledEmptyResults>
              ) : (
                visibleSections.map(([category, reports]) => (
                  <Layout.Stack key={category} gap="spacing-3">
                    {showSectionHeadings && (
                      <Text variant="heading-lg-bold" color="color-text-primary">
                        {category}
                      </Text>
                    )}
                    <Layout.Stack gap="spacing-4">
                      {reports.map(report => (
                        <Layout.Box
                          key={report.uuid}
                          backgroundColor="color-background-primary"
                          borderColor="color-border"
                          borderWidth="border-thin"
                          padding="none"
                        >
                          <Layout.Group padding="spacing-5" gap="spacing-5" align="flex-start">
                            <StyledReportImage src={report.thumbnailUrl} alt="" />
                            <Layout.Stack gap="spacing-2" style={{ flex: 1, minWidth: 0 }}>
                              <Text variant="heading-md-bold" color="color-text-primary">
                                {report.reportName}
                              </Text>
                              <Text variant="body-md" color="color-text-secondary">
                                <StyledReportDescription>
                                  <Markdown>{report.description}</Markdown>
                                </StyledReportDescription>
                              </Text>
                            </Layout.Stack>
                          </Layout.Group>
                          <Divider />
                          <Layout.Group paddingX="spacing-5" paddingY="spacing-3" gap="spacing-2">
                            <Button
                              variant="secondary"
                              leading={<Icon name="send" />}
                              onClick={() => {
                                setZendeskDialogRequestType(RequestType.NewRequest)
                                setIsZendeskDialogOpen(true)
                              }}
                            >
                              Request report
                            </Button>
                            <Button
                              variant="tertiary"
                              leading={<Icon name="eye" />}
                              href={
                                report.demoWithoutData ? undefined : `/demo-reports/${report.uuid}`
                              }
                              disabled={report.demoWithoutData}
                              onClick={e => {
                                if (report.demoWithoutData) return

                                e.preventDefault()

                                navigate(`/demo-reports/${report.uuid}`)
                              }}
                            >
                              Preview
                            </Button>
                          </Layout.Group>
                        </Layout.Box>
                      ))}
                    </Layout.Stack>
                  </Layout.Stack>
                ))
              )}
            </Layout.Stack>
          </StyledContentColumn>
        </Page.Body>
      </Page.Content>

      {zendeskDialogRequestType && (
        <ZendeskDialog
          requestType={zendeskDialogRequestType}
          open={isZendeskDialogOpen}
          onClose={() => {
            setIsZendeskDialogOpen(false)
          }}
        />
      )}
    </AppLayout>
  )
}

export default AnalyticsLibrary
